import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { ACCEPTANCE_FILTERS } from '@/lib/notifications/labels';
import type { AcceptanceFilter } from '@/lib/notifications/types';

// Roster acceptance-gate filter (N11 requirement 4): lets the operator narrow the
// PNR roster to who is awaiting acceptance vs accepted vs declined, so they can
// work the gate. A controlled chip bar — the owning tab holds the state and
// applies `matchesAcceptanceFilter` to its rows. Counts are optional.

interface NotificationAcceptanceFilterProps {
  value: AcceptanceFilter;
  onChange: (next: AcceptanceFilter) => void;
  /** Optional per-bucket counts shown on the chips. */
  counts?: Partial<Record<AcceptanceFilter, number>>;
}

export function NotificationAcceptanceFilter({
  value,
  onChange,
  counts,
}: NotificationAcceptanceFilterProps) {
  const { t } = useTranslation('pnr');
  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      role="group"
      aria-label={t('acceptance.filterLabel')}
      data-testid="acceptance-filter"
    >
      {ACCEPTANCE_FILTERS.map((f) => {
        const active = value === f;
        const count = counts?.[f];
        return (
          <button
            key={f}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(f)}
            data-testid={`acceptance-filter-${f}`}
            className={cn(
              'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
              active
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input bg-background text-muted-foreground hover:text-foreground',
            )}
          >
            {t(`acceptance.filter.${f}`)}
            {typeof count === 'number' ? (
              <span className={cn('ml-1', active ? 'opacity-90' : 'opacity-70')}>{count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
